import seo from '@/core/data/landingSeo.json'

const L = seo.locales.en

export default {
  hero: L.hero,
  startFree: "Get Started Free",
  features: {
    altTemplate: "Visual demonstration of {title} feature",
    items: {
      learnStyle: {
        title: "Learn Styles from References",
        description:
          "Select a favorite reference photo to deconstruct its underlying tone, color, and mood.",
      },
      seeGaps: {
        title: "See and Decode Gaps",
        description:
          "Understand differences in tone, color, and saturation with interactive charts and gap cards.",
      },
      aiAnalysis: {
        title: "Understand First, Edit Later",
        description:
          "AI analyzes visual evidence to explain style formation and suggest your next moves.",
      },
      applyStyle: {
        title: "Adapt Styles to Your Photos",
        description:
          "Evaluate your image's raw conditions and decide which style elements are best to transfer.",
      },
    },
  },
  advantages: {
    title: "From Understanding Style to Creative Expression",
    items: {
      styleLearning: {
        title: "Learn the Style, Don't Just Copy",
        description:
          "Treat reference photos as learning gateways rather than rigid templates. PhotoScope deconstructs the underlying tone, color, and mood to help you grasp the rules of visual aesthetics.",
      },
      gapVisualization: {
        title: "See the Gap, Decode the Reason",
        description:
          "Translate abstract aesthetic concepts into intuitive visual data. Highlight essential differences using clear charts and structured gap cards, clarifying exactly where your image stands.",
      },
      aiInsights: {
        title: "Understand Why, Plan How",
        description:
          "Go beyond raw diagnostics with AI-guided explanations of how each visual gap affects the mood. Receive structured post-processing priorities that turn complex observations into actionable steps.",
      },
      styleAdaptation: {
        title: "Adapt Wisely, Protect Character",
        description:
          "Focus on knowledge transfer rather than blind replication. Learn how to adapt a reference style to your own image's raw conditions, elevating visual quality while preserving your unique voice.",
      },
    },
  },
  steps: {
    title: "How It Works: Decode and Adapt Any Style in Four Steps",
    items: {
      upload: {
        title: "Upload & Choose Reference",
        description:
          "Upload your photo, then select a reference image whose style and mood you wish to deconstruct.",
      },
      analyze: {
        title: "Visualize the Gap",
        description:
          "Identify key differences and editing priorities using dual-compare views, interactive charts, and structured style gap cards.",
      },
      edit: {
        title: "Read the Guide & Edit",
        description:
          "Read a detailed post-processing guide containing AI analysis, then apply these editing directions to your own photo using your favorite editor.",
      },
      review: {
        title: "Upload After & Review",
        description:
          "Submit your edited version to receive review feedback (coming soon) and generate side-by-side Before/After cards to save or share.",
      },
    },
  },
  useCases: {
    title: "Built for every stage of your photography learning",
    items: {
      beginners: {
        title: "For Beginners",
        description: {
          0: "Choose a favorite reference photo to easily grasp professional tone and color patterns.",
          1: "Use intuitive charts and gap cards to understand why your photos look different.",
          2: "Learn fundamental photography terms through clear, AI-guided editing directions.",
        },
      },
      enthusiasts: {
        title: "For Enthusiasts",
        description: {
          0: "Deconstruct your favorite visual styles to study their color, layers, and atmosphere.",
          1: "Instantly locate the critical aesthetic gaps between your work and the reference style.",
          2: "Translate artistic intuition into reasoned edits, saving time and reducing trial-and-error.",
        },
      },
      aesthetic: {
        title: "For Aesthetic Seekers",
        description: {
          0: "Explore the underlying visual order and emotional expression behind different styles.",
          1: "Analyze how color cast, tone curves, and saturation join together to build an atmosphere.",
          2: "Convert abstract aesthetic feelings into structured, comparable, and actionable analysis.",
        },
      },
      practical: {
        title: "For Practical Photographers",
        description: {
          0: "Set clear visual targets with reference photos to avoid directionless editing.",
          1: "Focus on high-priority adjustments, such as highlight controls or color temperature.",
          2: "Upload your edited version to review progress with side-by-side Before/After cards.",
        },
      },
    },
  },
  testimonials: {
    title: "What Our Users Say",
    items: {
      sarah: {
        name: "Sarah Chen",
        content:
          "I used to know a reference looked better, but not why. PhotoScope showed me the tone and color gaps so I knew where to start.",
      },
      michael: {
        name: "Michael Rodriguez",
        content:
          "It doesn’t push me to copy the reference. It helps me see which differences matter most and what to adjust first.",
      },
      emma: {
        name: "Emma Thompson",
        content:
          "I like how it turns mood into visible charts and clear explanations. Every comparison makes my visual decisions sharper.",
      },
    },
  },
  faq: L.faq,
  footer: {
    copyright: "© 2026 PhotoScope. All rights reserved.",
    product: {
      title: "Product",
      photoscope: "PhotoScope",
      beforeAfter: "Before & After"
    },
    resources: {
      title: "Resources",
      pricing: "Pricing",
      blog: "Blog"
    },
    company: {
      title: "Company",
      contact: "Contact Us",
      terms: "Terms of Use",
      privacy: "Privacy Policy"
    }
  },
  pricing: {
    billing: {
      monthly: "Monthly",
      yearly: "Yearly",
    },
    badge: {
      yearlyDiscount: "Up to {percent}% off",
    },
    card: {
      recommended: "Recommended",
      currentPlan: "Current Plan",
      perMonth: "/month",
      save: "Save {percentage}%",
      discountPercent: "{percentage}%",
      billedAnnually: "billed annually, {price}/year",
    },
    button: {
      subscribe: "Subscribe",
      buyOnce: "Buy Once",
      getStarted: "Get Started",
    },
    tiers: {
      free: "Free",
      standard: "Standard",
      pro: "Pro",
    },
    section: {
      title: "Choose Your Plan",
      subtitle: "Start free, upgrade when you need more.",
    },
    cta: "Get Started",
  },
  blog: {
    section: {
      title: "Latest Articles",
      subtitle: "Explore insights and tips to master photo analysis",
      viewAll: "View All Articles",
    },
    categories: {
      Guides: "Guides",
      Concepts: "Concepts",
    },
    card: {
      publishedOn: "Published on",
    },
    cta: {
      title: "Read your own photo with PhotoScope",
      button: "Upload your photo",
      subtitle: "Compare it with any reference image and turn visual differences into clear, actionable insights.",
    },
  },
};
